/**
 * Which language the page is in.
 *
 * Three, and no more: English is what the code, the API and the harness are
 * written in and the only one every reader here shares; Polish and Finnish are
 * where the people using this instance sit. A fourth is a catalogue away, but
 * an empty catalogue is worse than no entry - i18next falls back to English key
 * by key, so a half-translated language reads as a bug rather than as a choice.
 *
 * Stored the same way as the colour scheme, and for the same reason: the same
 * account is read on a bench monitor in the lab and on a laptop at home, and a
 * browser is a better unit for "which language" than a server-side profile
 * would be for a page a visitor may open before signing in at all.
 */
export const LANGUAGES = [
  { code: "en", name: "English", short: "EN" },
  { code: "pl", name: "Polski", short: "PL" },
  { code: "fi", name: "Suomi", short: "FI" },
] as const;

export type Lang = (typeof LANGUAGES)[number]["code"];

export const FALLBACK: Lang = "en";

export const KEY = "hilc.lang";

export function isLang(v: unknown): v is Lang {
  return LANGUAGES.some((l) => l.code === v);
}

/**
 * The stored choice, or the browser's, or English.
 *
 * `navigator.languages` is consulted in order rather than just `language`,
 * because a machine set to en-GB with Polish second should get Polish only if
 * English is not on the list at all - the first MATCH wins, not the first
 * entry we happen to recognise further down.
 *
 * Region is dropped (`pl-PL` -> `pl`): there is one Polish catalogue, and a
 * browser that asks for pl-PL and gets English because of four characters is
 * the kind of failure nobody reports, they just live with it.
 */
export function detect(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (isLang(v)) return v;
  } catch {
    // Private mode, or storage disabled. Fall through to the browser.
  }
  const wanted =
    typeof navigator !== "undefined"
      ? navigator.languages?.length
        ? navigator.languages
        : [navigator.language]
      : [];
  for (const w of wanted) {
    const base = String(w || "").toLowerCase().split("-")[0];
    if (isLang(base)) return base;
  }
  return FALLBACK;
}

export function store(lang: Lang): void {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    // Not fatal: the choice applies for this page load and is forgotten.
  }
}

/**
 * Put the language on <html>.
 *
 * Not decoration. `lang` is what a screen reader picks a voice from - an
 * English synthesiser reading "Ławka zajęta" is unintelligible - and what the
 * browser hyphenates and spell-checks by. It is also what `Intl` in this app
 * is keyed to via i18next, so leaving it at the index.html default would put
 * the document and its own text in two different languages.
 */
export function applyLang(lang: Lang): void {
  if (typeof document !== "undefined") document.documentElement.lang = lang;
}
